import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkle, UsersThree, UserPlus, Copy, Trash, Crown, Heart, Eye, ArrowRight, Info } from '@phosphor-icons/react';

const ROLE_LABELS = {
  owner: { label: 'Owner', icon: Crown, color: '#C5A059', description: 'Full control, can invite/remove members' },
  spouse: { label: 'Spouse / Partner', icon: Heart, color: '#C53030', description: 'Co-owner of household finances' },
  advisor: { label: 'Read-Only Advisor', icon: Eye, color: '#4A5568', description: 'View-only access for financial advisors' },
};

const Household = () => {
  const [household, setHousehold] = useState(null);
  const [role, setRole] = useState(null);
  const [members, setMembers] = useState([]);
  const [combinedStats, setCombinedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('spouse');
  const [inviteUrl, setInviteUrl] = useState('');

  useEffect(() => { fetchHousehold(); }, []);

  const fetchHousehold = async () => {
    try {
      const response = await api.get('/household');
      setHousehold(response.data.household);
      setRole(response.data.role);
      setMembers(response.data.members || []);
      
      if (response.data.household) {
        try {
          const statsRes = await api.get('/household/combined-stats');
          setCombinedStats(statsRes.data);
        } catch (e) {
          // Ignore stats error
        }
      }
    } catch (error) {
      toast.error('Failed to load household');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newHouseholdName.trim()) {
      toast.error('Please enter a household name');
      return;
    }
    try {
      await api.post('/household', { name: newHouseholdName });
      toast.success('Household created!');
      setNewHouseholdName('');
      fetchHousehold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create household');
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }
    try {
      const response = await api.post('/household/invite', {
        invitee_email: inviteEmail,
        role: inviteRole
      });
      const fullUrl = `${window.location.origin}${response.data.invite_url}`;
      setInviteUrl(fullUrl);
      toast.success('Invitation created!');
      setInviteEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    }
  };

  const handleCopyInvite = () => {
    navigator.clipboard.writeText(inviteUrl);
    toast.success('Invite link copied!');
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the household?')) return;
    try {
      await api.delete(`/household/members/${userId}`);
      toast.success('Member removed');
      fetchHousehold();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleDeleteHousehold = async () => {
    if (!window.confirm('Delete the entire household? This cannot be undone.')) return;
    try {
      await api.delete('/household');
      toast.success('Household deleted');
      setHousehold(null);
      setMembers([]);
      setCombinedStats(null);
    } catch (error) {
      toast.error('Failed to delete household');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96 text-[#4A5568]">Loading...</div>;
  }

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Household Planning:</strong> Combine financial data across spouses, partners, and family. Read-only advisor access lets your financial professional view but not edit your data. <span className="text-[#1A202C]">The Combined Income / Expenses / Net Worth cards below are auto-calculated</span> — each member enters their own data on the source pages linked under each card.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Household Planning
        </h1>
        <p className="text-base text-[#4A5568]">Joint financial planning for couples and families</p>
      </div>

      {!household ? (
        // Create Household
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-8 dashboard-card max-w-2xl" data-testid="create-household-form">
          <div className="flex items-center gap-3 mb-4">
            <UsersThree size={28} weight="duotone" className="text-[#C5A059]" />
            <h2 className="cabinet-grotesk text-2xl font-medium text-[#0A1128]">Create a Household</h2>
          </div>
          <p className="text-sm text-[#4A5568] mb-6">
            Start a household to share financial planning with your spouse, partner, or family. You can invite a spouse and optionally a read-only advisor.
          </p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="household_name">Household Name</Label>
              <Input
                id="household_name"
                data-testid="household-name-input"
                placeholder="The Smith Family"
                value={newHouseholdName}
                onChange={(e) => setNewHouseholdName(e.target.value)}
                className="border-slate-300"
              />
            </div>
            <Button onClick={handleCreate} data-testid="create-household-button" className="bg-[#0A1128] hover:bg-[#152145]">
              Create Household
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Household Info */}
          <div className="bg-[#0A1128] text-white rounded-md p-6 dashboard-card" data-testid="household-card">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#A0AABF] mb-2">Your Household</p>
                <h2 className="cabinet-grotesk text-2xl font-medium text-[#C5A059]">{household.name}</h2>
                <p className="text-sm text-[#A0AABF] mt-1">{members.length} member{members.length !== 1 ? 's' : ''}</p>
              </div>
              {role === 'owner' && (
                <Button onClick={handleDeleteHousehold} data-testid="delete-household-button" variant="outline" className="border-[#C53030] text-[#C53030] hover:bg-[#C53030]/10">
                  <Trash size={16} className="mr-2" /> Delete Household
                </Button>
              )}
            </div>
          </div>

          {/* Combined Stats */}
          {combinedStats && combinedStats.member_summaries.length > 0 && (
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-md p-4 dashboard-card" data-testid="household-data-entry-guide">
                <div className="flex items-start gap-3">
                  <Info size={20} weight="duotone" className="text-[#0A1128] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#1A202C] mb-1">Where do I enter my financial data?</p>
                    <p className="text-xs text-[#4A5568] mb-3 leading-relaxed">
                      Combined totals roll up from per-member entries on three source pages. Click any tile below to enter or update your numbers — each household member should do this on their own account.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Link
                        to="/income-sources"
                        data-testid="goto-income-sources"
                        className="flex items-center justify-between border border-slate-200 hover:border-[#C5A059] hover:bg-[#C5A059]/5 rounded-md p-3 transition-colors group"
                      >
                        <div>
                          <p className="text-xs font-semibold text-[#0A1128]">Monthly Income</p>
                          <p className="text-xs text-[#4A5568]">Income Sources</p>
                        </div>
                        <ArrowRight size={16} className="text-[#C5A059] group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                      <Link
                        to="/spending-tracker"
                        data-testid="goto-spending-tracker"
                        className="flex items-center justify-between border border-slate-200 hover:border-[#C5A059] hover:bg-[#C5A059]/5 rounded-md p-3 transition-colors group"
                      >
                        <div>
                          <p className="text-xs font-semibold text-[#0A1128]">Monthly Expenses</p>
                          <p className="text-xs text-[#4A5568]">Spending Tracker</p>
                        </div>
                        <ArrowRight size={16} className="text-[#C5A059] group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                      <Link
                        to="/net-worth"
                        data-testid="goto-net-worth"
                        className="flex items-center justify-between border border-slate-200 hover:border-[#C5A059] hover:bg-[#C5A059]/5 rounded-md p-3 transition-colors group"
                      >
                        <div>
                          <p className="text-xs font-semibold text-[#0A1128]">Net Worth</p>
                          <p className="text-xs text-[#4A5568]">Net Worth Tracker</p>
                        </div>
                        <ArrowRight size={16} className="text-[#C5A059] group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <Link to="/income-sources" data-testid="combined-income-card" className="bg-white border border-slate-200 hover:border-[#C5A059] rounded-md shadow-sm p-5 dashboard-card transition-colors group">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Combined Income</p>
                  <p className="text-2xl font-semibold text-[#0A1128] ibm-plex">${combinedStats.monthly_income.toLocaleString()}</p>
                  <p className="text-xs text-[#4A5568] mt-1">per month</p>
                  <p className="text-xs text-[#C5A059] mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Add income <ArrowRight size={12} /></p>
                </Link>
                <Link to="/spending-tracker" data-testid="combined-expenses-card" className="bg-white border border-slate-200 hover:border-[#C5A059] rounded-md shadow-sm p-5 dashboard-card transition-colors group">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Combined Expenses</p>
                  <p className="text-2xl font-semibold text-[#C53030] ibm-plex">${combinedStats.monthly_expenses.toLocaleString()}</p>
                  <p className="text-xs text-[#4A5568] mt-1">per month</p>
                  <p className="text-xs text-[#C5A059] mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Add expense <ArrowRight size={12} /></p>
                </Link>
                <Link to="/net-worth" data-testid="combined-net-worth-card" className="bg-white border border-slate-200 hover:border-[#C5A059] rounded-md shadow-sm p-5 dashboard-card transition-colors group">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Combined Net Worth</p>
                  <p className="text-2xl font-semibold text-[#C5A059] ibm-plex">${combinedStats.total_net_worth.toLocaleString()}</p>
                  <p className="text-xs text-[#4A5568] mt-1">total</p>
                  <p className="text-xs text-[#C5A059] mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Add snapshot <ArrowRight size={12} /></p>
                </Link>
                <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Household WHS™</p>
                  <p className="text-2xl font-semibold text-[#2D6A4F] ibm-plex">{combinedStats.combined_wealth_health_score}/100</p>
                  <p className="text-xs text-[#4A5568] mt-1">averaged</p>
                </div>
              </div>

              {/* Per-Member Summary */}
              <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="member-summary-table">
                <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Individual Progress</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Member</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Income/mo</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Retirement</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">Net Worth</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568]">WHS™</th>
                      </tr>
                    </thead>
                    <tbody>
                      {combinedStats.member_summaries.map(m => (
                        <tr key={m.user_id} className="border-b border-slate-100">
                          <td className="py-3 px-4 text-sm font-medium">{m.full_name}</td>
                          <td className="py-3 px-4 text-sm text-right ibm-plex">${m.monthly_income.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-right ibm-plex">${m.total_retirement.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-right ibm-plex font-semibold">${m.net_worth.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-[#C5A059] ibm-plex">{m.wealth_health_score} ({m.letter_grade})</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Members List */}
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
              <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Members</h2>
              <div className="space-y-3">
                {members.map(member => {
                  const RoleConfig = ROLE_LABELS[member.role];
                  const Icon = RoleConfig.icon;
                  return (
                    <div key={member.user_id} className="flex items-center justify-between border border-slate-200 rounded-md p-3" data-testid={`member-${member.user_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md" style={{ backgroundColor: `${RoleConfig.color}15` }}>
                          <Icon size={20} weight="duotone" style={{ color: RoleConfig.color }} />
                        </div>
                        <div>
                          <p className="font-medium text-[#0A1128]">{member.full_name}</p>
                          <p className="text-xs text-[#4A5568]">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium px-2 py-1 rounded" style={{ color: RoleConfig.color, backgroundColor: `${RoleConfig.color}15` }}>
                          {RoleConfig.label}
                        </span>
                        {role === 'owner' && member.role !== 'owner' && (
                          <button onClick={() => handleRemoveMember(member.user_id)} className="text-[#C53030] hover:text-[#9B2C2C]">
                            <Trash size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Invite (owner only) */}
            {role === 'owner' && (
              <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="invite-form">
                <div className="flex items-center gap-2 mb-4">
                  <UserPlus size={22} weight="duotone" className="text-[#C5A059]" />
                  <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128]">Invite Member</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="invite_email">Email Address</Label>
                    <Input
                      id="invite_email"
                      type="email"
                      data-testid="invite-email-input"
                      placeholder="spouse@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="invite_role">Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger data-testid="invite-role-select" className="border-slate-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spouse">Spouse / Partner</SelectItem>
                        <SelectItem value="advisor">Read-Only Advisor</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#4A5568] mt-2">{ROLE_LABELS[inviteRole].description}</p>
                  </div>
                  <Button onClick={handleInvite} data-testid="send-invite-button" className="w-full bg-[#0A1128] hover:bg-[#152145]">
                    Create Invitation
                  </Button>

                  {inviteUrl && (
                    <div className="bg-[#C5A059]/10 border border-[#C5A059] rounded-md p-3 mt-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Invitation Link</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-[#1A202C] font-mono break-all flex-1">{inviteUrl}</p>
                        <Button onClick={handleCopyInvite} data-testid="copy-invite-button" size="sm" variant="outline" className="border-[#C5A059] text-[#0A1128]">
                          <Copy size={14} />
                        </Button>
                      </div>
                      <p className="text-xs text-[#4A5568] mt-2">Share this link with the invitee. They must sign up with the same email address.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Household;
